import { useState, useContext, useEffect } from 'react'

import { MainLayout } from '../layouts'
import { TodoContentBox } from '../components/misc'
import { DocumentsContext, UserContext, ValidationContext } from '../context'
import { ClickAwayListener } from '@mui/material'
import { Sort2 } from '../assets/icons'
import { ChevronRight } from '@mui/icons-material'
import { getSorting, saveSortingToLS } from '../utils'
import { DateInputV2 } from '../components/ui_new'

const SORTING_PERSISTENCE_KEY = 'todo-list'

const ToDoList = () => {

  const { user } = useContext(UserContext)
  const { myValidations, validationsFetched, todoActiveSort, setTodoActiveSort } = useContext(ValidationContext)
  const { documents } = useContext(DocumentsContext)
  const [tasks, setTasks] = useState([])
  const [tasksReady, setTasksReady] = useState(false)
  const [createdTasksToValidate, setCreatedTasksToValidate] = useState([])
  const [myTasksToValidate, setMyTasksToValidate] = useState([])
  const [tasksToCorrect, setTasksToCorrect] = useState([])
  const [tasksValidated, setTasksValidated] = useState([])
  const [tasksCancelled, setTasksCancelled] = useState([])

  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [dateAfter, setDateAfter] = useState(null)
  const [dateBefore, setDateBefore] = useState(null)

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates
    setDateAfter(start)
    setDateBefore(end)
  }

  const handleDateRangeClear = () => {
    setDateAfter(null)
    setDateBefore(null)
  }

  const sortOptions = {
      'name': { label: 'Nom', asc: 'A > Z', desc: 'Z > A' },
      'date': { label: 'Date', asc: '+ ancienne', desc: ' + récente' },
    }

  useEffect(() => {
    if (!validationsFetched) {
      setTasksReady(false)
      return
    }
    const validationsArray = []
    for (const key in myValidations) {
      validationsArray.push({
        ...myValidations[key],
        id: key
      })
    }
    const dateForTask = (task) => {
      if(task.status === 'pending') return task.deadline || null
      if(task.status === 'validated' || task.status === 'refused') {
        return task.validators.find(v => v.submitted)?.submitted || null
      }
      return task.metadata?.updated || null
    }

    const sortTasksBy = (key, order) => (a, b) => {
      let aValue = a[key]
      let bValue = b[key]
      if (key === 'date') {
        aValue = dateForTask(a) || 0
        bValue = dateForTask(b) || 0
      } else if (key === 'name') {
        aValue = (documents[a.documentId]?.name || a.documentName || '').toLowerCase()
        bValue = (documents[b.documentId]?.name || b.documentName || '').toLowerCase()
      }
      if (aValue < bValue) return order === 'asc' ? -1 : 1
      if (aValue > bValue) return order === 'asc' ? 1 : -1
      return 0
    }

    const filterTasksByDateRange = (task) => {
      if(!dateAfter || !dateBefore) return true
      const taskDate = dateForTask(task)
      if(!taskDate) return false
      const date = new Date(taskDate)
      return date >= dateAfter && date <= dateBefore
    }
    const createdTasksToValidateFiltered = validationsArray
      .filter(validation => validation.status === 'pending' && (`${validation.metadata?.userId}` === `${user?.id}`))
      .toSorted(sortTasksBy(todoActiveSort.activeItem, todoActiveSort[todoActiveSort.activeItem]))
      .filter(filterTasksByDateRange)
    const myTasksToValidateFiltered = validationsArray.filter(validation => validation.status === 'pending' && (validation.validatorIds?.includes(`${user?.id}`)))
      .toSorted(sortTasksBy(todoActiveSort.activeItem, todoActiveSort[todoActiveSort.activeItem]))
      .filter(filterTasksByDateRange)
    const tasksToCorrectFiltered = validationsArray.filter(validation => validation.status === 'refused')
      .toSorted(sortTasksBy(todoActiveSort.activeItem, todoActiveSort[todoActiveSort.activeItem]))
      .filter(filterTasksByDateRange)
    const tasksValidatedFiltered = validationsArray.filter(validation => validation.status === 'validated')
      .toSorted(sortTasksBy(todoActiveSort.activeItem, todoActiveSort[todoActiveSort.activeItem]))
      .filter(filterTasksByDateRange)
    const tasksCancelledFiltered = validationsArray.filter(validation => validation.status === 'cancelled')
      .toSorted(sortTasksBy(todoActiveSort.activeItem, todoActiveSort[todoActiveSort.activeItem]))
      .filter(filterTasksByDateRange)

    setCreatedTasksToValidate(createdTasksToValidateFiltered)
    setMyTasksToValidate(myTasksToValidateFiltered)
    setTasksToCorrect(tasksToCorrectFiltered)
    setTasksValidated(tasksValidatedFiltered)
    setTasksCancelled(tasksCancelledFiltered)
    setTasks(validationsArray)
    setTasksReady(true)

  }, [myValidations, validationsFetched, todoActiveSort, dateAfter, dateBefore, documents, user])


  // Handle sort
  const onSort = (sort_by, order) => {
    setTodoActiveSort((prev) => ({ ...prev, [sort_by]: order, activeItem: sort_by }))
    saveSortingToLS(sort_by, order, SORTING_PERSISTENCE_KEY)
  }

  useEffect(() => {
    let sort = 'date'
    let order = 'desc'
    const sortLS = getSorting()
    if (sortLS && sortLS[SORTING_PERSISTENCE_KEY]) {
      sort = sortLS[SORTING_PERSISTENCE_KEY].value
      order = sortLS[SORTING_PERSISTENCE_KEY].order
    }
    setTodoActiveSort((prev) => ({ ...prev, [sort]: order }))
  }, [validationsFetched, setTodoActiveSort])

  return (
    <MainLayout className="page-todo" fullHeader={true} showSearch={true} searchIn="todo" pageTitle={"To do list"} hideTitleOnDesktop={true}>
      <div className="todo-content__main">
        <div className={`todo-content__boxes-wrapper`}>
          <div className="todo-content__boxes">
            <div className='column-parent'>
              <div className="todo-content__header">
                <div className='todo-content__header-title desktop'>
                  To do list
                  <span>{tasks.length} actions</span>
                </div>
                <div className='filters-section-parent !w-auto !mb-0'>

                  <div className={`filters-section__item show`}>
                    <DateInputV2
                      selectsRange={true}
                      placeholder="Après - avant"
                      onChange={handleDateRangeChange}
                      onChangeRaw={(e) => e.preventDefault()}
                      isClearable={dateAfter && dateBefore}
                      onClearableClick={handleDateRangeClear}
                      // tooltip={!dateBefore && !dateAfter ? "" : `${dateAfter ? `Créé après : ${dateAfter.toLocaleDateString()}` : ''} ${dateBefore ? ` - Créé avant : ${dateBefore.toLocaleDateString()}` : ''}`}
                      className={`date-input-v2--light date-input-v2--small date-input-v2--squared date-input-v2--width-medium`}
                    />
                  </div>
                  <ClickAwayListener onClickAway={() => {
                    setShowSortDropdown(false)
                  }
                  }>
                    <div className="relative">
                      <div
                        className={`filters-section-sort !h-8 !max-w-full select-v2 select-v2--filled position-relative ${showSortDropdown && 'active'}`}
                      >
                        <div className="flex md:px-3 h-8 items-center gap-2" onClick={() => setShowSortDropdown(!showSortDropdown)}>
                          <div className="flex gap-2">
                            <Sort2 />
                            <span className='sort-label sm-d-none'>
                              {sortOptions[todoActiveSort.activeItem]?.label} {sortOptions[todoActiveSort.activeItem]?.[todoActiveSort[todoActiveSort.activeItem]]}
                            </span>
                          </div>
                          <span className='chevron-icon' ><ChevronRight fontSize='small' /></span>
                        </div>
                      </div>
                      {showSortDropdown && <div className={`sort-dropdown select-v2__body_dropdown align-right`}>
                        {Object.entries(sortOptions).map((option, index) => (
                          <div key={index} className={`dropdown-item`} >
                            <p> {option[1].label} <ChevronRight fontSize={'small'} /> </p>
                            <ul>
                              <li
                                className={`dropdown-item ${todoActiveSort[option[0]] === "asc" ? 'active' : ''}`}
                                onClick={() => {
                                  onSort(option[0], 'asc')
                                  setShowSortDropdown(false)
                                }}>
                                {option[1].asc}
                              </li>
                              <li
                                className={`dropdown-item ${todoActiveSort[option[0]] === "desc" ? 'active' : ''}`}
                                onClick={() => {
                                  onSort(option[0], 'desc')
                                  setShowSortDropdown(false)
                                }}>
                                {option[1].desc}
                              </li>
                            </ul>
                          </div>
                        ))}
                      </div>}
                    </div>
                  </ClickAwayListener>
                </div>
              </div>
              <div className={`container-element ${user?.validation_access === 'manager' ? 'validator' : ''}`}>
                
                <div className={`column`}>

                  <TodoContentBox
                    title={"En revision"}
                    emptyLabel={'Aucune action de validation créée en attente'}
                    actions={createdTasksToValidate}
                    loading={!tasksReady}
                    color="yellow"
                  />
                </div>

                {user?.validation_access === 'manager' &&
                  <div className={`column`}>
                    <TodoContentBox
                      title={"À valider"}
                      emptyLabel={'Aucune action de validation en cours'}
                      actions={myTasksToValidate}
                      loading={!tasksReady}
                      color="yellow"
                    />
                  </div>
                }

                <div className={`column`}>
                  <TodoContentBox
                    title="À corriger"
                    emptyLabel={'Aucune action de validation à corriger'}
                    actions={tasksToCorrect}
                    loading={!tasksReady}
                    color="red"
                  />
                </div>

                <div className={`column`}>
                  <TodoContentBox
                    title="Validé"
                    emptyLabel={'Aucune action de validation validée'}
                    actions={tasksValidated}
                    loading={!tasksReady}
                    color="green"
                  />
                </div>

                <div className={`column`}>
                  <TodoContentBox
                    title="Annulé"
                    emptyLabel={'Aucune action de validation n\'a été annulée'}
                    actions={tasksCancelled}
                    loading={!tasksReady}
                    color="gray"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}

export default ToDoList
